import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { getMyLogo, saveMyLogo, deleteMyLogo } from '../../api/users'
import { getPartyPresetLogo } from '../../components/PartyPresetsPage'
import {
  INITIAL_LOGO_SELECTION,
  applyManualLogo,
  applySavedLogo,
  beginManualUpload,
  clearLogoSelection,
  dropSavedLogo,
  invalidatePartyLogo,
  logoForPayload,
  markManualLogoSaved,
  resolvePartyLogo,
  selectPartyLogo,
} from './logoSelection'
import type { LogoSelection } from './logoSelection'

/** Logo-keuze van de Testdata Generator: geen logo, logo van de gekozen
 * relatie, handmatige upload, of het expliciet gekozen opgeslagen
 * standaardlogo. De beslisregels (incl. bescherming tegen late
 * relatielogo-antwoorden) staan in `logoSelection.ts`; deze hook beheert
 * alleen de React-state, de token-teller en de API-aanroepen.
 *
 * Het opgeslagen standaardlogo wordt per gebruiker opgehaald maar nooit
 * automatisch toegepast — alleen via `selectSavedLogo`. Een gebruikerswissel
 * wist de keuze. */
export function useLogoState(userId: number | null | undefined) {
  const { t } = useTranslation()
  const [selection, setSelection] = useState<LogoSelection>(INITIAL_LOGO_SELECTION)
  const [savedLogoBase64, setSavedLogoBase64] = useState<string | null>(null)
  const [saveLogoDefault, setSaveLogoDefault] = useState(false)
  const [logoSaving, setLogoSaving] = useState(false)
  const logoInputRef = useRef<HTMLInputElement>(null)
  // Strikt oplopende tokenbron; zie logoSelection.ts voor de regels.
  const tokenRef = useRef(INITIAL_LOGO_SELECTION.token)
  const nextToken = () => {
    tokenRef.current += 1
    return tokenRef.current
  }

  const resetFileInput = () => {
    if (logoInputRef.current) logoInputRef.current.value = ''
  }

  useEffect(() => {
    const token = nextToken()
    setSelection(clearLogoSelection(token))
    setSavedLogoBase64(null)
    setSaveLogoDefault(false)
    if (!userId) return
    let cancelled = false
    getMyLogo().then(res => {
      if (!cancelled && res.has_logo && res.logo_base64) setSavedLogoBase64(res.logo_base64)
    }).catch(() => { /* niet kritisch */ })
    return () => { cancelled = true }
  }, [userId])

  const handleLogoUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return
    const token = nextToken()
    setSelection(beginManualUpload(token))
    const saveAsDefault = saveLogoDefault
    const reader = new FileReader()
    reader.onload = (ev) => {
      const result = ev.target?.result as string
      const base64 = result.split(',')[1]
      setSelection(s => applyManualLogo(s, token, base64, file.name))
      if (saveAsDefault) {
        setLogoSaving(true)
        saveMyLogo(base64)
          .then(() => {
            setSavedLogoBase64(base64)
            setSelection(s => markManualLogoSaved(s, base64))
          })
          .catch(() => { /* niet kritisch */ })
          .finally(() => setLogoSaving(false))
      }
    }
    reader.readAsDataURL(file)
  }

  const clearLogo = () => {
    const token = nextToken()
    setSelection(clearLogoSelection(token))
    resetFileInput()
  }

  const selectSavedLogo = () => {
    if (!savedLogoBase64) return
    const token = nextToken()
    setSelection(applySavedLogo(token, savedLogoBase64, t('test_data_generator.logo_saved_filename')))
    resetFileInput()
  }

  const toggleSaveLogoDefault = (checked: boolean) => {
    setSaveLogoDefault(checked)
    const base64 = selection.base64
    if (checked && selection.source === 'manual' && base64) {
      setLogoSaving(true)
      saveMyLogo(base64)
        .then(() => {
          setSavedLogoBase64(base64)
          setSelection(s => markManualLogoSaved(s, base64))
        })
        .catch(() => { /* niet kritisch */ })
        .finally(() => setLogoSaving(false))
    }
  }

  const removeSavedLogo = () => {
    setLogoSaving(true)
    deleteMyLogo()
      .then(() => {
        setSavedLogoBase64(null)
        setSaveLogoDefault(false)
        const token = nextToken()
        setSelection(s => dropSavedLogo(s, token))
      })
      .catch(() => { /* niet kritisch */ })
      .finally(() => setLogoSaving(false))
  }

  /** Relatie gekozen als leverancier (dropdown of herstel van de onthouden
   * keuze): het vorige logo vervalt direct; het relatielogo telt pas zodra
   * het antwoord voor déze, nog steeds geselecteerde relatie binnen is. */
  const selectParty = (partyId: number, partyHasLogo: boolean) => {
    const token = nextToken()
    setSelection(selectPartyLogo(token, partyId, partyHasLogo))
    resetFileInput()
    if (!partyHasLogo) return
    const fileName = t('test_data_generator.logo_from_party_filename')
    getPartyPresetLogo(partyId)
      .then(res => {
        const base64 = res.has_logo && res.logo_base64 ? res.logo_base64 : null
        setSelection(s => resolvePartyLogo(s, token, partyId, base64, fileName))
      })
      .catch(() => setSelection(s => resolvePartyLogo(s, token, partyId, null, fileName)))
  }

  /** Leveranciersidentiteit handmatig gewijzigd of relatiekeuze gewist. */
  const invalidateParty = () => {
    const token = nextToken()
    setSelection(s => invalidatePartyLogo(s, token))
  }

  /** Leverancier vervangen buiten de relatiekeuze om (sjabloon, historie,
   * configuratiepreset): geen enkel eerder logo hoort nog bij die leverancier. */
  const resetForSupplierChange = () => {
    const token = nextToken()
    setSelection(clearLogoSelection(token))
    resetFileInput()
  }

  return {
    logoBase64: selection.pending ? null : selection.base64,
    logoFileName: selection.fileName,
    logoPending: selection.pending,
    logoIsFromSaved: selection.source === 'saved',
    logoIsFromParty: selection.source === 'party',
    payloadLogoBase64: logoForPayload(selection),
    logoInputRef,
    hasSavedLogo: savedLogoBase64 !== null,
    saveLogoDefault, logoSaving,
    handleLogoUpload, clearLogo, selectSavedLogo, toggleSaveLogoDefault, removeSavedLogo,
    selectParty, invalidateParty, resetForSupplierChange,
  }
}

export type LogoState = ReturnType<typeof useLogoState>
